import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import { Button } from '@/components/ui/button'
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group'
import { Icon, ICONS } from '@/lib/icons'
import type { KnockoutBracket, KnockoutMatch, Player, Sport } from '@/types/api'
import { apiClient, handleApiError } from '@/services/api'
import { getSportStrategy } from '@/lib/sport-strategies'

interface ReportKnockoutMatchDialogProps {
  bracketId: string
  open: boolean
  onOpenChange: (open: boolean) => void
  match: KnockoutMatch | null
  playersById: Record<string, Player>
  sport: Sport
  setsToPlay?: number
  onReported: (payload: { match: KnockoutMatch; bracket: KnockoutBracket }) => void
}

export function ReportKnockoutMatchDialog({
  bracketId,
  open,
  onOpenChange,
  match,
  playersById,
  sport,
  setsToPlay,
  onReported,
}: ReportKnockoutMatchDialogProps) {
  const { t } = useTranslation()
  const [scoreA, setScoreA] = useState('')
  const [scoreB, setScoreB] = useState('')
  const [outcome, setOutcome] = useState<'A' | 'B' | 'DRAW'>('A')
  const [date, setDate] = useState('')
  const [time, setTime] = useState('')
  const [submitting, setSubmitting] = useState(false)

  const isChess = sport === 'SPORT_CHESS'

  useEffect(() => {
    if (!open || !match) {
      return
    }

    // Default scores to "0" instead of empty string for better UX
    setScoreA('0')
    setScoreB('0')

    const playedAt = match.matchTime ? new Date(match.matchTime) : new Date()
    const localDate = playedAt.toISOString().slice(0, 10)
    const localTime = playedAt.toTimeString().slice(0, 5)
    setDate(localDate)
    setTime(localTime)
  }, [open, match])

  if (!match) {
    return null
  }

  const playerAName = match.playerAId
    ? playersById[match.playerAId]?.displayName || t('players.unknownPlayer', 'Unknown player')
    : t('knockout.match.tbd', 'TBD')

  const playerBName = match.playerBId
    ? playersById[match.playerBId]?.displayName || t('players.unknownPlayer', 'Unknown player')
    : t('knockout.match.tbd', 'TBD')

  // Check if current scores are valid (for enabling/disabling submit button)
  const isScoreValid = () => {
    if (isChess) {
      // Chess outcome is always valid (one of three radio options)
      return true
    }

    // For other sports, parse scores
    const parsedA = Number.parseFloat(scoreA)
    const parsedB = Number.parseFloat(scoreB)

    if (Number.isNaN(parsedA) || Number.isNaN(parsedB)) {
      return false
    }

    // Use sport-specific strategy to validate
    const strategy = getSportStrategy(sport)
    const validation = strategy.validateScore(parsedA, parsedB, {
      isKnockout: true,
      setsToPlay: setsToPlay
    })

    return validation.valid
  }

  const validateScores = () => {
    let parsedA: number
    let parsedB: number

    if (isChess) {
      // For chess, convert outcome to internal encoding
      if (outcome === 'A') {
        parsedA = 2 // Win
        parsedB = 0 // Loss
      } else if (outcome === 'B') {
        parsedA = 0 // Loss
        parsedB = 2 // Win
      } else {
        parsedA = 1 // Draw
        parsedB = 1 // Draw
      }
    } else {
      // For other sports, parse the entered scores
      parsedA = Number.parseFloat(scoreA)
      parsedB = Number.parseFloat(scoreB)

      if (Number.isNaN(parsedA) || Number.isNaN(parsedB)) {
        toast.error(t('knockout.match.validation.scores', 'Enter a valid score for both players'))
        return null
      }
    }

    // Use sport-specific validation with knockout context (no draws allowed for most sports)
    const strategy = getSportStrategy(sport)
    const validation = strategy.validateScore(parsedA, parsedB, {
      isKnockout: true,
      setsToPlay: setsToPlay
    })
    if (!validation.valid) {
      toast.error(t(validation.errorKey))
      return null
    }

    return { scoreA: parsedA, scoreB: parsedB }
  }

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault()

    const validated = validateScores()
    if (!validated) {
      return
    }

    const matchTime = date && time ? new Date(`${date}T${time}:00`).toISOString() : undefined

    try {
      setSubmitting(true)
      const response = await apiClient.reportKnockoutMatch(bracketId, match.id, {
        scoreA: validated.scoreA,
        scoreB: validated.scoreB,
        matchTime
      })
      toast.success(t('knockout.match.reported', 'Match result saved'))
      onReported(response)
      onOpenChange(false)
    } catch (error) {
      handleApiError(error, (apiError) => {
        toast.error(apiError.message || t('errors.generic'))
      })
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-lg">
            <Icon icon={ICONS.edit} width={20} height={20} />
            {t('knockout.match.reportTitle', 'Report match result')}
          </DialogTitle>
          <DialogDescription>
            {t('knockout.match.reportDescription', 'Enter the score to record the winner of this matchup.')}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-5">
          <div className="space-y-3">
            <div className="rounded-md border bg-muted/40 p-3 text-sm">
              <div className="font-semibold text-foreground">{playerAName}</div>
              <div className="text-muted-foreground">vs.</div>
              <div className="font-semibold text-foreground">{playerBName}</div>
            </div>

            {isChess ? (
              <div className="space-y-3">
                <Label>{t('matches.outcome.label', 'Match result')}</Label>
                <RadioGroup value={outcome} onValueChange={(value) => setOutcome(value as 'A' | 'B' | 'DRAW')}>
                  <div className="flex items-center space-x-3 rounded-md border border-border bg-background p-3">
                    <RadioGroupItem value="A" id="knockout_outcome_a" />
                    <Label htmlFor="knockout_outcome_a" className="flex-1 cursor-pointer">
                      {playerAName} {t('common.wins', 'vinner')}
                    </Label>
                  </div>
                  <div className="flex items-center space-x-3 rounded-md border border-border bg-background p-3">
                    <RadioGroupItem value="B" id="knockout_outcome_b" />
                    <Label htmlFor="knockout_outcome_b" className="flex-1 cursor-pointer">
                      {playerBName} {t('common.wins', 'vinner')}
                    </Label>
                  </div>
                  <div className="flex items-center space-x-3 rounded-md border border-border bg-background p-3">
                    <RadioGroupItem value="DRAW" id="knockout_outcome_draw" />
                    <Label htmlFor="knockout_outcome_draw" className="flex-1 cursor-pointer">
                      {t('matches.outcome.draw', 'Oavgjort')}
                    </Label>
                  </div>
                </RadioGroup>
              </div>
            ) : (
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="knockout-score-a">{playerAName}</Label>
                  <Input
                    id="knockout-score-a"
                    type="number"
                    min={getSportStrategy(sport).getScoreDisplay().minScore}
                    max={getSportStrategy(sport).getScoreDisplay().maxScore}
                    step={getSportStrategy(sport).getScoreDisplay().stepScore}
                    placeholder={getSportStrategy(sport).getScoreDisplay().scorePlaceholder}
                    value={scoreA}
                    onChange={(event) => setScoreA(event.target.value)}
                    onInput={(event) => setScoreA((event.target as HTMLInputElement).value)}
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="knockout-score-b">{playerBName}</Label>
                  <Input
                    id="knockout-score-b"
                    type="number"
                    min={getSportStrategy(sport).getScoreDisplay().minScore}
                    max={getSportStrategy(sport).getScoreDisplay().maxScore}
                    step={getSportStrategy(sport).getScoreDisplay().stepScore}
                    placeholder={getSportStrategy(sport).getScoreDisplay().scorePlaceholder}
                    value={scoreB}
                    onChange={(event) => setScoreB(event.target.value)}
                    onInput={(event) => setScoreB((event.target as HTMLInputElement).value)}
                    required
                  />
                </div>
              </div>
            )}

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="knockout-date">{t('matches.played_at_date')}</Label>
                <Input
                  id="knockout-date"
                  type="date"
                  value={date}
                  onChange={(event) => setDate(event.target.value)}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="knockout-time">{t('matches.played_at_time')}</Label>
                <Input
                  id="knockout-time"
                  type="time"
                  value={time}
                  onChange={(event) => setTime(event.target.value)}
                />
              </div>
            </div>
          </div>

          <DialogFooter className="flex justify-end gap-2 sm:justify-end">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              {t('common.cancel')}
            </Button>
            <Button 
              type="submit" 
              disabled={submitting || !isScoreValid()}
            >
              {submitting ? t('common.saving') : t('knockout.match.save', 'Save result')}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}
